import React, { useMemo, useState } from 'react';
import { filter } from 'lodash';
import {
  DateRangeFilter,
  Link,
  RenderFilteredTable,
  RenderTableColumn,
  RenderTableOnChange,
  TextFilterSearch,
  TextSearchTableFilter,
} from '@common/components';
import { Col, Row, Typography } from 'antd';
import moment from 'moment';

import { useQuery } from '@common/hooks';
import {
  localStorageStore,
  useStoredFilters,
} from 'works/legacy/lib/hooks/use-stored-filters';
import { useFilterCallbacks } from 'works/legacy/lib/hooks/use-filter-callbacks';
import {
  CLEANUP_WHEN_EMPTY,
  EMPTY_PLACEHOLDER,
  formatDateWithTz,
  formatHiringLeaders,
  formatOrganizationProfilesEEIDs,
  formatSecondsToDuration,
  formatTimesheetDates,
  isPastApproval,
  nonFalse,
} from '@timekeeping/services';
import {
  WORKER_TIMESHEET_LABEL_OVERWRITE,
  WorkerTimesheetStatus,
  WorkerTimesheetActionButtons,
} from '@timekeeping/components';
import {
  ManagerWorkerTimesheets,
  WorkerTimesheetFilters,
} from '@timekeeping/graphql';
import {
  WorkerTimesheetListFragment,
  WorkerTimesheetStatusCountFragment,
  WorksTimekeepingWorkerTimesheetFiltersType,
  WorksTimekeepingWorkerTimesheetSortType,
  WorksTimekeepingWorkerTimesheetStatusCountId,
} from '@common/graphql/schema';
import { Spacing } from 'works/legacy/lib/style-helper';

type Sort = WorksTimekeepingWorkerTimesheetSortType;

type Pagination = { offset?: number; limit: number };

type CountID = WorksTimekeepingWorkerTimesheetStatusCountId;

const { Text } = Typography;

const defaultPageSize = 50;

const statusWithActions = new Set<CountID>(['all', 'submitted', 'approved']);

const statusWithApprovalDeadline = new Set<CountID>([
  'all',
  'submitted',
  'approved',
]);

const statusWithoutHiringLeader = new Set<CountID>([
  'not_submitted',
  'overdue',
]);

const defaultOrder = (count: WorkerTimesheetStatusCountFragment): Sort => {
  switch (count.id) {
    case 'not_submitted':
    case 'approved':
    case 'processing':
    case 'invoiced':
    case 'rejected':
      return { direction: 'DESC', field: 'timesheetPeriod' };
    case 'overdue':
    case 'expired':
      return { direction: 'ASC', field: 'timesheetPeriod' };
    case 'submitted':
      return { direction: 'ASC', field: 'defaultSubmitted' };
  }

  return { direction: 'DESC', field: 'default' };
};

export const WorkerTimesheetsTab = ({
  entity,
  count,
}: {
  entity: 'organization' | 'supplier';
  count: WorkerTimesheetStatusCountFragment;
}) => {
  const { data: filterOptions } = useQuery(WorkerTimesheetFilters);
  const [sort, setSort] = useState(() => defaultOrder(count));
  const [pagination, setPagination] = useState<Pagination>({
    limit: defaultPageSize,
  });
  const [filters, setFilters] = useStoredFilters(
    `WorkerTimesheetsTable-${count.id}`,
    {
      store: localStorageStore,
      defaults: {} as WorksTimekeepingWorkerTimesheetFiltersType,
    }
  );

  const effectiveFilters = useMemo(() => {
    if (count.id === 'all') {
      return filters;
    }

    return { ...filters, status: [count.id] };
  }, [count.id, filters]);

  const { onClearFilter, onClearAllFilters } = useFilterCallbacks(setFilters);

  const { data, loading } = useQuery(ManagerWorkerTimesheets, {
    notifyOnNetworkStatusChange: true,
    variables: {
      filters: {
        sort: sort,
        filters: effectiveFilters,
      },
      limit: pagination.limit,
      offset: pagination.offset,
    },
  });

  const page = data?.page;
  const workerTimesheets = page?.timesheets || [];
  const statusesFilter = filterOptions?.options?.statuses || [];

  const tablePageInfo = useMemo(
    () => ({
      showSizeChanger: true,
      defaultPageSize: defaultPageSize,
      total: data?.page.totalCount,
    }),
    [data?.page.totalCount]
  );

  const workerTimesheetsColumns = filter<
    RenderTableColumn<WorkerTimesheetListFragment> | false,
    RenderTableColumn<WorkerTimesheetListFragment>
  >(
    [
      {
        title: 'Name',
        key: 'employeeName',
        className: 'name-col sentry-mask',
        sorter: true,
        filtered: !!filters.employeeName?.length,
        filteredValue: filters.employeeName || CLEANUP_WHEN_EMPTY,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              autoFocus
              id="filter-by-employee-name-input"
              placeHolder="Filter by name"
              onSearch={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
            />
          </section>
        ),
        render: (_: unknown, value: WorkerTimesheetListFragment) => {
          if (value.status === 'overdue' || value.status === 'not_submitted') {
            return value.worksMembership.name;
          }

          return (
            <Text>
              <Link
                to={
                  entity === 'organization'
                    ? `/works/timecard/${value.id}`
                    : `/works/supplier/timecard/${value.id}`
                }
              >
                {value.worksMembership.name}
              </Link>
            </Text>
          );
        },
      },
      {
        title: 'EEID',
        key: 'employeeId',
        filtered: !!filters.employeeId?.length,
        filteredValue: filters.employeeId || CLEANUP_WHEN_EMPTY,
        filterDropdown: TextSearchTableFilter,
        sorter: true,
        className: 'eeid-col',
        render: (_: unknown, value: WorkerTimesheetListFragment) => (
          <Text>
            {formatOrganizationProfilesEEIDs(value.organizationProfiles || [])}
          </Text>
        ),
      },
      {
        title: 'Total Time',
        key: 'totalTime',
        className: 'total-time-col',
        sorter: true,
        render: (_: unknown, value: WorkerTimesheetListFragment) => {
          if (value.status === 'overdue') {
            return EMPTY_PLACEHOLDER;
          }

          return <Text>{formatSecondsToDuration(value.totalSeconds)}</Text>;
        },
      },
      {
        title: 'Timesheet Period',
        key: 'timesheetPeriod',
        className: 'timesheet-period-col',
        sorter: true,
        filterFormat: 'dateRange',
        filtered: !!filters.timesheetPeriod?.length,
        filteredValue: filters.timesheetPeriod || CLEANUP_WHEN_EMPTY,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <DateRangeFilter
              autoFocus
              onSearch={(value) => {
                setSelectedKeys((value || []) as string[]);
                confirm();
              }}
            />
          </section>
        ),
        render: (_: unknown, value: WorkerTimesheetListFragment) => (
          <Text>
            {formatTimesheetDates(
              value.timesheet.startDate,
              value.timesheet.endDate,
              value.timesheet.timeZone
            )}
          </Text>
        ),
      },
      !statusWithoutHiringLeader.has(count.id) && {
        title: 'Hiring Leaders',
        key: 'hiringLeaderName',
        className: 'hiring-leader-col',
        sorter: true,
        filtered: !!filters.hiringLeaderName?.length,
        filteredValue: filters.hiringLeaderName || CLEANUP_WHEN_EMPTY,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              autoFocus
              id="filter-by-hiring-leader-name-input"
              placeHolder="Filter by name"
              onSearch={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
            />
          </section>
        ),
        render: (_: unknown, value: WorkerTimesheetListFragment) => {
          if (value.status === 'overdue') {
            return;
          }

          return <Text>{formatHiringLeaders(value.hiringLeaders)}</Text>;
        },
      },
      statusWithApprovalDeadline.has(count.id) && {
        title: 'Approval Deadline',
        key: 'approvalDeadline',
        className: 'approval-deadline-col',
        sorter: true,
        render: (_: unknown, value: WorkerTimesheetListFragment) => (
          <Col>
            <Row>
              <Text>
                {value.submittedAtTimesheet?.approvalDeadline &&
                  formatDateWithTz(
                    moment(value.submittedAtTimesheet.approvalDeadline),
                    value.timesheet.timeZone
                  )}
              </Text>
            </Row>
            {isPastApproval(value) && (
              <Row>
                <Text strong className="color-coral">
                  Past approval deadline
                </Text>
              </Row>
            )}
          </Col>
        ),
      },
      count.id === 'all' && {
        title: 'Status',
        key: 'status',
        dataIndex: 'status',
        className: 'status-col',
        sorter: true,
        filtered: !!filters.status?.length,
        filteredValue: filters.status || CLEANUP_WHEN_EMPTY,
        filters: [
          ...statusesFilter.map((filter) => ({
            text: WORKER_TIMESHEET_LABEL_OVERWRITE[filter] || filter,
            value: filter,
          })),
        ],
        render: (text) => <WorkerTimesheetStatus status={text} />,
      },
      statusWithActions.has(count.id) && {
        title: ' ',
        key: 'actions',
        className: 'actions-col',
        render: (_: unknown, value: WorkerTimesheetListFragment) => (
          <WorkerTimesheetActionButtons mode="compact" timesheet={value} />
        ),
      },
    ],
    nonFalse
  );

  const handleTableChange: RenderTableOnChange<WorkerTimesheetListFragment> = (
    newPagination,
    newFilters,
    sorter
  ) => {
    if (sorter instanceof Array) {
      return;
    }
    const pageSize = newPagination.pageSize || pagination.limit;
    const sortField =
      (sorter.order && sorter.columnKey) || defaultOrder(count)['field'];

    setFilters((oldFilters) => ({
      ...oldFilters,
      ...newFilters,
    }));
    setSort({
      direction: sorter.order === 'descend' ? 'DESC' : 'ASC',
      field: sortField as Sort['field'],
    });
    setPagination({
      offset: ((newPagination.current || 1) - 1) * pageSize,
      limit: pageSize,
    });
  };

  return (
    <div className="WorkerTimesheetsPage">
      <RenderFilteredTable
        rowKey="id"
        dataSource={workerTimesheets}
        columns={workerTimesheetsColumns}
        loading={loading}
        onChange={handleTableChange}
        pagination={tablePageInfo}
        filters={filters}
        onClearFilter={onClearFilter}
        onClearAllFilters={onClearAllFilters}
        rowClassName={(row) =>
          isPastApproval(row) ? 'highlight-action-required' : ''
        }
      />
      <style jsx>{`
        .WorkerTimesheetsPage :global(.ant-table-row) {
          height: 60px;
        }
        .WorkerTimesheetsPage :global(.past-approval-info) {
          padding-top: 4px;
          padding-bottom: 8px;
        }
        .WorkerTimesheetsPage :global(.ant-tabs-tab) {
          min-width: 114px;
        }
      `}</style>
    </div>
  );
};
